// components/booking/BookingWizard.tsx
import React, { useState } from 'react';
import { University, Course, Client } from '../../types/index.ts';
import { BookingDateStep } from './BookingDateStep.tsx';
import { BookingTimeStep } from './BookingTimeStep.tsx';
import { BookingAuthStep } from './BookingAuthStep.tsx';
import { BookingDetailsStep } from './BookingDetailsStep.tsx';
import { BookingConfirmStep } from './BookingConfirmStep.tsx';
import { BookingSuccessStep } from './BookingSuccessStep.tsx';

export interface BookingData {
  // Date & Time
  selectedDate: string;
  selectedTime: string;
  userTimezone: string;
  
  // Authentication
  client: Client | null;
  
  // Booking Details
  university: string;
  courseNotListed: boolean;
  courseId: number | null;
  manualCourseName: string;
  departmentId: number | null;
  notes: string;
  
  // NEW: Exam type
  examType: string;
  
  // NEW: Alternative phone
  useAlternativePhone: boolean;
  alternativePhone: string;
  
  // Files
  sessionId: string;
  uploadedFiles: any[];
}

interface BookingWizardProps {
  onBackToMain?: () => void; // Optional callback to return to main selection
}

export const BookingWizard: React.FC<BookingWizardProps> = ({ onBackToMain }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Generate unique session ID
  const [sessionId] = useState(() => `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  const [bookingData, setBookingData] = useState<BookingData>({
    selectedDate: '',
    selectedTime: '',
    userTimezone: 'Asia/Kuwait', // Default to Kuwait
    client: null,
    university: '',
    courseNotListed: false,
    courseId: null,
    manualCourseName: '',
    departmentId: null,
    notes: '',
    examType: '', // NEW
    useAlternativePhone: false, // NEW
    alternativePhone: '', // NEW
    sessionId,
    uploadedFiles: []
  });

  const [createdBooking, setCreatedBooking] = useState<any>(null);

  const updateBookingData = (updates: Partial<BookingData>) => {
    setBookingData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    setCurrentStep(prev => prev + 1);
    setError('');
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setError('');
  };

  const handleDateSelect = (date: string) => {
    updateBookingData({ selectedDate: date });
    handleNext();
  };

  const handleTimeSelect = (time: string, timezone: string) => {
    updateBookingData({ 
      selectedTime: time, 
      userTimezone: timezone 
    });
    handleNext();
  };

  const handleAuthenticated = (client: Client) => {
    updateBookingData({ client });
    handleNext();
  };

  const handleDetailsComplete = (details: any) => {
    updateBookingData({
      university: details.university,
      courseNotListed: details.courseNotListed,
      courseId: details.courseId,
      manualCourseName: details.manualCourseName,
      departmentId: details.departmentId,
      notes: details.notes,
      examType: details.examType, // NEW
      useAlternativePhone: details.useAlternativePhone, // NEW
      alternativePhone: details.alternativePhone, // NEW
      uploadedFiles: details.uploadedFiles
    });
    handleNext();
  };

  const handleBookingSuccess = (booking: any) => {
    setCreatedBooking(booking);
    handleNext();
  };

  const handleStartOver = () => {
    if (onBackToMain) {
      onBackToMain();
    } else {
      setCurrentStep(1);
      setBookingData({
        selectedDate: '',
        selectedTime: '',
        userTimezone: 'Asia/Kuwait',
        client: null,
        university: '',
        courseNotListed: false,
        courseId: null,
        manualCourseName: '',
        departmentId: null,
        notes: '',
        examType: '', // NEW
        useAlternativePhone: false, // NEW
        alternativePhone: '', // NEW
        sessionId: `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        uploadedFiles: []
      });
      setCreatedBooking(null);
      setError('');
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1: return 'اختر التاريخ';
      case 2: return 'اختر الوقت';
      case 3: return 'تسجيل الدخول';
      case 4: return 'تفاصيل الحجز';
      case 5: return 'مراجعة وتأكيد';
      case 6: return 'تم الحجز';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">حجز جلسة تدريس</h1>
          <p className="text-gray-600">احجز جلسة تدريس خاصة مع أفضل المدرسين</p>
        </div>

        {/* Progress Indicator */}
        {currentStep < 6 && (
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4, 5].map((step) => (
                <div key={step} className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium border-2 ${
                      step <= currentStep
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-400 border-gray-300'
                    }`}
                  >
                    {step}
                  </div>
                  <div
                    className={`mt-2 text-xs text-center max-w-20 ${
                      step <= currentStep ? 'text-blue-600 font-medium' : 'text-gray-400'
                    }`}
                  >
                    {getStepTitle(step)}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 5) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="text-red-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="mr-3 text-red-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-lg overflow-visible">
          {currentStep === 1 && (
            <BookingDateStep
              selectedDate={bookingData.selectedDate}
              onDateSelect={handleDateSelect}
              setError={setError}
              setLoading={setLoading}
            />
          )}

          {currentStep === 2 && (
            <BookingTimeStep
              selectedDate={bookingData.selectedDate}
              selectedTime={bookingData.selectedTime}
              userTimezone={bookingData.userTimezone}
              onTimeSelect={handleTimeSelect}
              onBack={handleBack}
              setError={setError}
              setLoading={setLoading}
            />
          )}

          {currentStep === 3 && (
            <BookingAuthStep
              onAuthenticated={handleAuthenticated}
              onBack={handleBack}
              setError={setError}
              setLoading={setLoading}
            />
          )}

          {currentStep === 4 && (
            <BookingDetailsStep
              bookingData={bookingData}
              onDetailsComplete={handleDetailsComplete}
              onBack={handleBack}
              setError={setError}
              setLoading={setLoading}
            />
          )}

          {currentStep === 5 && (
            <BookingConfirmStep
              bookingData={bookingData}
              onBookingSuccess={handleBookingSuccess}
              onBack={handleBack}
              setError={setError}
              setLoading={setLoading}
            />
          )}

          {currentStep === 6 && (
            <BookingSuccessStep
              bookingData={bookingData}
              createdBooking={createdBooking}
              onStartOver={handleStartOver}
            />
          )}
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center space-x-4 space-x-reverse">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-gray-700">جاري المعالجة...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};